import { BLACK_BLURHASH } from '../avatar'
import { gameKey, gameListKey, gameRelevantListKey } from '../keys'
import { useQueryClient } from '@tanstack/react-query'
import {
  type GameGetSuccess,
  type DecoratedGameParticipant,
  type GameAward,
  type GameData,
  type GameId,
  type GamePrize,
  type GameParticipantRemoveInput,
} from '@terros/common'

export type GameCache = {
  getListItem: (gameId?: GameId) => GameData | undefined
  getItem: (gameId?: GameId) => GameGetSuccess | undefined
  clearList: () => void
  clearItem: (gameId: GameId) => void
  clearListItem: (gameId: GameId) => void
  addListItem: (gameData: GameData) => void
  updateGameData: (gameData: GameData) => void
  updateParticipant: (gameId: GameId, participant: DecoratedGameParticipant) => void
  updateCover: (gameId: GameId) => void
  removeCover: (gameId: GameId) => void
  removeParticipant: (gameId: GameId, participantId: GameParticipantRemoveInput['participantId']) => void
}

export function useGameCache(): GameCache {
  const queryClient = useQueryClient()
  // Who sees a game depends on its participants, and its group on its status and dates, so the ranked pages are
  // refetched rather than patched.
  const clearRelevantList = (): void => {
    queryClient.invalidateQueries({ queryKey: gameRelevantListKey() })
  }
  return {
    clearList: () => {
      queryClient.invalidateQueries({ queryKey: gameListKey() })
      clearRelevantList()
    },
    clearListItem: (gameId: GameId) => {
      clearRelevantList()
      queryClient.setQueriesData<GameData[]>({ queryKey: gameListKey() }, (old: GameData[] | undefined) => {
        const previous = old || []
        const modified = previous.filter((g) => g.gameId !== gameId)
        return modified
      })
    },
    clearItem: (gameId: GameId) => {
      clearRelevantList()
      queryClient.invalidateQueries({ queryKey: gameKey(gameId) })
    },
    addListItem: (gameData: GameData) => {
      clearRelevantList()
      queryClient.setQueriesData<GameData[]>({ queryKey: gameListKey() }, (old: GameData[] | undefined) => {
        const previous = old || []
        const modified = [...previous, gameData]
        return modified
      })
    },
    updateGameData: (gameData: GameData) => {
      clearRelevantList()
      queryClient.setQueryData<GameGetSuccess>(gameKey(gameData.gameId), (prev: GameGetSuccess | undefined) => {
        if (!prev) return
        return { ...prev, game: toNewGameData(gameData) }
      })
      queryClient.setQueriesData<GameData[]>({ queryKey: gameListKey() }, (old: GameData[] | undefined) => {
        const previous = old || []
        const modified = previous.map((g) => (g.gameId === gameData.gameId ? toNewGameData(gameData) : g))
        return modified
      })
    },
    getListItem: (gameId?: GameId): GameData | undefined => {
      if (!gameId) return
      return queryClient.getQueryData<GameData[]>(gameListKey())?.find((g) => gameId === g.gameId)
    },
    getItem: (gameId?: GameId): GameGetSuccess | undefined => {
      if (!gameId) return
      return queryClient.getQueryData<GameGetSuccess>(gameKey(gameId))
    },
    removeParticipant: (gameId: GameId, participantId: GameParticipantRemoveInput['participantId']) => {
      clearRelevantList()
      queryClient.setQueryData<GameGetSuccess>(gameKey(gameId), (prev: GameGetSuccess | undefined) => {
        if (!prev) return

        const participants = prev.participants
          .filter((p) => p.participantId !== participantId)
          .map((p) => {
            if (p.type === 'user') return p
            return {
              ...p,
              users: p.users.filter((user) => user.participantId !== participantId),
            }
          })

        return { ...prev, participants }
      })
    },
    updateCover: (gameId: GameId) => {
      clearRelevantList()
      queryClient.setQueryData<GameGetSuccess>(gameKey(gameId), (prev: GameGetSuccess | undefined) => {
        if (!prev) return
        return {
          ...prev,
          game: {
            ...prev.game,
            cover: updateCover(prev.game.cover),
          },
        }
      })
      queryClient.setQueryData<GameData[]>(gameListKey(), (old: GameData[] | undefined) => {
        if (!old) return
        return old.map((g) => (g.gameId === gameId ? { ...g, cover: updateCover(g.cover) } : g))
      })
    },
    removeCover: (gameId: GameId) => {
      clearRelevantList()
      queryClient.setQueryData<GameGetSuccess>(gameKey(gameId), (prev: GameGetSuccess | undefined) => {
        if (!prev) return
        return {
          ...prev,
          game: {
            ...prev.game,
            cover: {
              ...prev.game.cover,
              blurhash: undefined,
            },
          },
        }
      })
      queryClient.setQueriesData<GameData[]>({ queryKey: gameListKey() }, (old: GameData[] | undefined) => {
        const previous = old || []
        const modified = previous.map((g) =>
          g.gameId === gameId ? { ...g, cover: { ...g.cover, blurhash: undefined } } : g
        )
        return modified
      })
    },
    updateParticipant: (gameId: GameId, participant: DecoratedGameParticipant) => {
      clearRelevantList()
      queryClient.setQueryData<GameGetSuccess>(gameKey(gameId), (prev) => {
        if (!prev) return

        return {
          ...prev,
          participants: prev.participants.map((p) => {
            if (p.participantId === participant.participantId) {
              return participant
            }
            return p
          }),
        }
      })
    },
  }
}

export const toNewGameData = (game: GameData): GameData => {
  return {
    ...game,
    awards: updateAwards(game.awards),
    cover: toLatestCover(game.cover),
  }
}

const toLatestCover = (cover: GameData['cover']): GameData['cover'] => {
  if (!cover?.blurhash) return cover
  return {
    ...cover,
    download: cover.download + `?v=${Date.now()}`,
  }
}

const updateCover = (prev: GameData['cover']): GameData['cover'] => {
  if (!prev) return prev
  return {
    ...prev,
    blurhash: prev.blurhash ?? BLACK_BLURHASH,
    download: prev.download + `?v=${Date.now()}`,
  }
}

const updateAwards = (awards: GameAward[]): GameAward[] => {
  return awards.map((a) => ({
    ...a,
    prizes: a.prizes.map(updatePrize),
  }))
}

const updatePrize = (prize: GamePrize): GamePrize => {
  if (!prize.download) return prize
  return {
    ...prize,
    download: prize.download + `?v=${Date.now()}`,
  }
}
